/**
 * Сервис для работы с AiRecruiter Messenger API
 * Базовый сервис для управления кампаниями, диалогами, кандидатами и интервью
 */

import api from '../relevanter/services/api';
import type {
	Campaign,
	CampaignCreationCreditsCheck,
	CampaignLaunchCreditsCheck,
	CampaignStats,
	Dialogue,
	Message,
	Candidate,
	InterviewSlot,
	PaginatedResponse,
	GetCampaignsParams,
	GetDialoguesParams,
	SendMessageRequest,
	UpdateCandidateStatusRequest,
	UpdateInterviewStatusRequest,
	UpdateInterviewStatusResponse,
	CandidateStatus
} from '../types/aiRecruiter';

interface TenantRequestConfig {
	headers?: {
		'X-Tenant-Id'?: string | number;
	};
}

type UpdateCandidateStatusOptions = Pick<UpdateCandidateStatusRequest, 'sendDecisionNotification'>;

// ============= Campaign Methods =============

/**
 * Получить список кампаний
 */
export const getCampaigns = async (params: GetCampaignsParams = {}, config?: any): Promise<PaginatedResponse<Campaign>> => {
	// Extract tenantId from headers and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalParams = tenantId ? { ...params, tenantId } : params;
	const response = await api.get('/ai-recruiter/campaigns', { params: finalParams });
	return response.data;
};

/**
 * Получить кампанию по ID
 */
export const getCampaignById = async (campaignId: string | number, config?: any): Promise<Campaign> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}`, finalConfig);
	return response.data;
};

/**
 * Создать новую кампанию
 */
export const createCampaign = async (campaignData: Partial<Campaign>, config?: any): Promise<Campaign> => {
	// Extract tenantId from headers and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const companyId = config?.companyId;
	const finalParams = {
		...(tenantId ? { tenantId } : {}),
		...(companyId ? { companyId } : {}),
	};
	const finalConfig = Object.keys(finalParams).length > 0 ? { params: finalParams } : {};
	const response = await api.post('/ai-recruiter/campaigns', campaignData, finalConfig);
	return response.data;
};

/**
 * Обновить кампанию
 */
export const updateCampaign = async (
	campaignId: string | number,
	campaignData: Partial<Campaign>,
	config?: any
): Promise<Campaign> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.put(`/ai-recruiter/campaigns/${campaignId}`, campaignData, finalConfig);
	return response.data;
};

/**
 * Обновить шаблон первого сообщения.
 * tenantId обязателен — прокидываем через ?tenantId=X в query, как и остальные template-ручки.
 * Без него олавия бросает AccessDeniedException → INTERNAL_ERROR 500
 * (после убирания admin-mode в CampaignService.getCampaign).
 */
export const updateFirstMessageTemplate = async (
	campaignId: string | number,
	firstMessageTemplate: string,
	firstMessageTemplateClarify?: string,
	config?: any
): Promise<Campaign> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.patch(
		`/ai-recruiter/campaigns/${campaignId}/first-message-template`,
		{
			firstMessageTemplate,
			...(firstMessageTemplateClarify !== undefined ? { firstMessageTemplateClarify } : {})
		},
		finalConfig
	);
	return response.data;
};

export interface TemplateBundle {
	templateText?: string;
	clarifyTemplateText?: string;
	approvalTemplateText?: string;
	rejectionTemplateText?: string;
}

export const getAccountTemplate = async (config?: any): Promise<TemplateBundle> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const response = await api.get('/ai-recruiter/campaigns/account-template', tenantId ? { params: { tenantId } } : {});
	return response.data;
};

export const saveAccountTemplate = async (data: TemplateBundle, config?: any): Promise<TemplateBundle> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const response = await api.put('/ai-recruiter/campaigns/account-template', data, tenantId ? { params: { tenantId } } : {});
	return response.data;
};

export const getPositionTemplate = async (positionId: string | number, config?: any): Promise<TemplateBundle> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const response = await api.get(`/ai-recruiter/campaigns/position-template/${positionId}`, tenantId ? { params: { tenantId } } : {});
	return response.data;
};

export const savePositionTemplate = async (positionId: string | number, data: TemplateBundle, config?: any): Promise<TemplateBundle> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const response = await api.put(`/ai-recruiter/campaigns/position-template/${positionId}`, data, tenantId ? { params: { tenantId } } : {});
	return response.data;
};

/**
 * Удалить кампанию
 */
export const deleteCampaign = async (campaignId: string | number, config?: any): Promise<void> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	await api.delete(`/ai-recruiter/campaigns/${campaignId}`, finalConfig);
};

/**
 * Архивировать кампанию
 */
export const archiveCampaign = async (campaignId: string | number, config?: any): Promise<Campaign> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.post(`/ai-recruiter/campaigns/${campaignId}/archive`, {}, finalConfig);
	return response.data;
};

/**
 * Запустить кампанию
 */
export const startCampaign = async (campaignId: string | number, config?: any): Promise<Campaign> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const companyId = config?.companyId;
	const finalParams = {
		...(tenantId ? { tenantId } : {}),
		...(companyId ? { companyId } : {}),
	};
	const finalConfig = Object.keys(finalParams).length > 0 ? { params: finalParams } : {};
	const response = await api.post(`/ai-recruiter/campaigns/${campaignId}/start`, {}, finalConfig);
	return response.data;
};

export const checkCampaignCreationCredits = async (
	companyId: number,
	selectedCount: number,
	aiScreeningEnabled?: boolean,
	positionId?: string | number | null
): Promise<CampaignCreationCreditsCheck> => {
	const response = await api.post('/ai-recruiter/campaigns/credits/check-create', {
		companyId,
		...(positionId ? { positionId } : {}),
		selectedCount,
		aiScreeningEnabled
	});
	return response.data;
};

export const checkCampaignLaunchCredits = async (
	campaignId: string | number,
	companyId: number,
	config?: any
): Promise<CampaignLaunchCreditsCheck> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.post(`/ai-recruiter/campaigns/${campaignId}/credits/check-launch`, {
		companyId
	}, finalConfig);
	return response.data;
};

/**
 * Остановить кампанию
 */
export const stopCampaign = async (campaignId: string | number, config?: any): Promise<Campaign> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.post(`/ai-recruiter/campaigns/${campaignId}/stop`, {}, finalConfig);
	return response.data;
};

/**
 * Получить статистику кампании
 */
export const getCampaignStats = async (campaignId: string | number, config?: any): Promise<CampaignStats> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}/stats`, finalConfig);
	return response.data;
};

// ============= Dialogue Methods =============

/**
 * Сериализатор query-параметров: массивы → повторяющиеся ключи
 * (source=A&source=B), что ожидает Spring `@RequestParam List<T>`.
 * Делается именно для этого вызова — общий paramsSerializer в api.ts
 * использует arrayFormat: 'brackets' (`source[]=...`), который Spring не парсит.
 */
const serializeDialoguesParams = (params: Record<string, any>): string => {
	const sp = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === '') continue;
		if (Array.isArray(value)) {
			if (value.length === 0) continue;
			value.forEach((v) => sp.append(key, String(v)));
		} else {
			sp.append(key, String(value));
		}
	}
	return sp.toString();
};

/**
 * Получить диалоги кампании
 */
export const getCampaignDialogues = async (
	campaignId: string | number,
	params: GetDialoguesParams = {},
	config?: any
): Promise<PaginatedResponse<Dialogue>> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalParams = tenantId ? { ...params, tenantId } : params;
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}/dialogues`, {
		params: finalParams,
		paramsSerializer: serializeDialoguesParams,
	});
	return response.data;
};

export const getPositionDialogues = async (
	positionId: string | number,
	params: GetDialoguesParams = {},
	config?: any
): Promise<PaginatedResponse<Dialogue>> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalParams = tenantId ? { ...params, tenantId } : params;
	const response = await api.get(`/ai-recruiter/positions/${positionId}/dialogues`, {
		params: finalParams,
		paramsSerializer: serializeDialoguesParams,
	});
	return response.data;
};

/**
 * Получить сообщения диалога
 */
export const getDialogueMessages = async (
	campaignId: string | number,
	dialogueId: string | number,
	config?: any
): Promise<Message[]> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}/dialogues/${dialogueId}/messages`, finalConfig);
	// Handle both array and object responses
	if (Array.isArray(response.data)) {
		return response.data;
	}
	// If response is an object with messages field
	if (response.data && Array.isArray(response.data.messages)) {
		return response.data.messages;
	}
	// If response is an object with content field
	if (response.data && Array.isArray(response.data.content)) {
		return response.data.content;
	}
	// Fallback to empty array
	console.warn('Unexpected response format for getDialogueMessages:', response.data);
	return [];
};

/**
 * Отправить сообщение в диалог (ручное сообщение рекрутера)
 */
export const sendDialogueMessage = async (
	campaignId: string | number,
	dialogueId: string | number,
	message: string,
	config?: any
): Promise<Message> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.post(
		`/ai-recruiter/campaigns/${campaignId}/dialogues/${dialogueId}/send-message`,
		{ message } as SendMessageRequest,
		finalConfig
	);
	return response.data;
};

/**
 * Переключить состояние диалога (пауза/возобновление)
 * @param action - 'pause' | 'resume' | 'stop'
 */
export const toggleDialogue = async (
	campaignId: string | number,
	dialogueId: string | number,
	action: 'pause' | 'resume' | 'stop',
	config?: any
): Promise<Dialogue> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.post(`/ai-recruiter/campaigns/${campaignId}/dialogues/${dialogueId}/${action}`, {}, finalConfig);
	return response.data;
};

// ============= Candidate Methods =============

/**
 * Получить кандидатов кампании
 */
export const getCampaignCandidates = async (campaignId: string | number, config?: any): Promise<Candidate[]> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}/candidates`, finalConfig);
	// Обрабатываем как массив или пагинированный ответ
	const candidates = Array.isArray(response.data) ? response.data : response.data?.content || [];
	return candidates.map((candidate: Candidate) => ({
		...candidate,
		campaignId: candidate.campaignId ?? campaignId,
	}));
};

const normalizeCommunicationMethodUrl = (url: string): string => {
	const trimmedUrl = url.trim();
	if (/^https?:\/\//i.test(trimmedUrl)) {
		throw new Error('External communication URL is not allowed');
	}
	return trimmedUrl
		.replace(/^\/api\/v1\/ai-recruiter(\/|$)/, '/ai-recruiter$1')
		.replace(/^\/api\/ai-recruiter(\/|$)/, '/ai-recruiter$1');
};

export const openCandidateCommunicationMethod = async (url: string): Promise<string> => {
	const response = await api.get<{ url?: string; targetUrl?: string }>(
		normalizeCommunicationMethodUrl(url)
	);
	const targetUrl = response.data?.url || response.data?.targetUrl;
	if (!targetUrl) {
		throw new Error('Communication method target URL is empty');
	}
	return targetUrl;
};

/**
 * Обновить статус кандидата
 */
export const updateCandidateStatus = async (
	campaignId: string | number,
	candidateId: string | number,
	status: CandidateStatus,
	options?: UpdateCandidateStatusOptions,
	config?: any
): Promise<Candidate> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const request: UpdateCandidateStatusRequest = {
		status,
		...(options ?? {})
	};
	const response = await api.post(
		`/ai-recruiter/campaigns/${campaignId}/candidates/${candidateId}/status`,
		request,
		finalConfig
	);
	return response.data;
};

export const retryCampaignCandidateMessage = async (
	campaignId: string | number,
	candidateId: string | number,
	config?: TenantRequestConfig
): Promise<{
	success: boolean;
	candidateId: string;
	candidateRowId: string;
	newStatus: CandidateStatus;
	nextAttemptAt?: string | number[];
}> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.post(
		`/ai-recruiter/campaigns/${campaignId}/candidates/${candidateId}/retry`,
		{},
		finalConfig
	);
	return response.data;
};

// ============= Interview Slot Methods =============

/**
 * Получить слоты интервью для кампании
 */
export const getCampaignInterviewSlots = async (campaignId: string | number, config?: any): Promise<InterviewSlot[]> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}/interview-slots`, finalConfig);
	// Обрабатываем как массив или пагинированный ответ
	return Array.isArray(response.data) ? response.data : response.data?.content || response.data?.slots || [];
};

/**
 * Обновить статус слота интервью
 */
export const updateInterviewSlotStatus = async (
	campaignId: string | number,
	slotId: string | number,
	data: UpdateInterviewStatusRequest,
	config?: any
): Promise<InterviewSlot> => {
	// Extract tenantId from config and pass as query param
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.patch(`/ai-recruiter/campaigns/${campaignId}/interview-slots/${slotId}`, data, finalConfig);
	return response.data;
};

// ============= Position Interview Slots (по positionId) =============

/**
 * Получить все слоты интервью для позиции (по positionId)
 */
export const getPositionInterviewSlots = async (positionId: string | number, config?: any): Promise<InterviewSlot[]> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.get(`/ai-recruiter/positions/by-id/${positionId}/interview-slots`, finalConfig);
	return Array.isArray(response.data) ? response.data : response.data?.content || response.data?.slots || [];
};

/**
 * Обновить статус слота интервью по positionId
 */
export const updatePositionInterviewSlotStatus = async (
	positionId: string | number,
	slotId: string | number,
	data: UpdateInterviewStatusRequest,
	config?: any
): Promise<UpdateInterviewStatusResponse> => {
	const tenantId = config?.headers?.['X-Tenant-Id'];
	const finalConfig = tenantId ? { params: { tenantId } } : {};
	const response = await api.patch(`/ai-recruiter/positions/by-id/${positionId}/interview-slots/${slotId}`, data, finalConfig);
	return response.data;
};

// ============= Vacancy Page (Публичная страница вакансии) =============

/**
 * Получить детали вакансии для публичной страницы (устаревший, campaign-based)
 */
export const getVacancyDetails = async (campaignId: string | number, token?: string): Promise<any> => {
	const params = token ? { token } : {};
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}/vacancy`, { params });
	return response.data;
};

/**
 * Получить данные позиции по positionId (из business-back через ai-recruiter-back)
 */
export const getPositionInfo = async (positionId: string | number, token?: string): Promise<any> => {
	const params = token ? { token } : {};
	const response = await api.get(`/ai-recruiter/positions/${positionId}/info`, { params });
	return response.data;
};

/**
 * Получить доступные слоты для записи на интервью (устаревший, campaign-based)
 */
export const getAvailableSlots = async (campaignId: string | number, date: string): Promise<string[]> => {
	const response = await api.get(`/ai-recruiter/campaigns/${campaignId}/available-slots`, {
		params: { date }
	});
	return response.data;
};

/**
 * Получить доступные слоты по positionId
 */
export const getAvailableSlotsByPosition = async (positionId: string | number, date: string, recr?: string | null): Promise<string[]> => {
	const params: Record<string, string> = { date };
	if (recr) params.recr = recr;
	const response = await api.get(`/ai-recruiter/positions/${positionId}/available-slots`, { params });
	return response.data;
};

/**
 * Забронировать слот для интервью (устаревший, campaign-based)
 */
export const bookInterviewSlot = async (
	campaignId: string | number,
	data: {
		date: string;
		time: string;
		candidateName: string;
		candidatePhone: string;
		personalDataConsent: boolean;
		token?: string;
	}
): Promise<any> => {
	const response = await api.post(`/ai-recruiter/campaigns/${campaignId}/book-slot`, data);
	return response.data;
};

/**
 * Забронировать слот по positionId
 */
export const bookSlotByPosition = async (
	positionId: string | number,
	data: {
		date: string;
		time: string;
		candidateName: string;
		candidatePhone: string;
		personalDataConsent: boolean;
		token?: string;
		recr?: string;
	}
): Promise<any> => {
	const response = await api.post(`/ai-recruiter/positions/${positionId}/book-slot`, data);
	return response.data;
};

// ============= Active Conversations Check =============

/**
 * Проверить, есть ли активные беседы для списка HH resume ID
 */
export const checkActiveConversations = async (hhResumeIds: string[]): Promise<string[]> => {
	const response = await api.post('/ai-recruiter/campaigns/check-active-conversations', { hhResumeIds });
	return response.data?.activeResumeIds || [];
};

// ============= Экспорт всех методов одним объектом =============

const aiRecruiterService = {
	// Campaigns
	getCampaigns,
	getCampaignById,
	createCampaign,
	checkCampaignCreationCredits,
	updateCampaign,
	deleteCampaign,
	archiveCampaign,
	startCampaign,
	checkCampaignLaunchCredits,
	stopCampaign,
	getCampaignStats,

	// Dialogues
	getCampaignDialogues,
	getPositionDialogues,
	getDialogueMessages,
	sendDialogueMessage,
	toggleDialogue,

	// Candidates
	getCampaignCandidates,
	openCandidateCommunicationMethod,
	updateCandidateStatus,
	retryCampaignCandidateMessage,

	// Interview Slots
	getCampaignInterviewSlots,
	updateInterviewSlotStatus,
	getPositionInterviewSlots,
	updatePositionInterviewSlotStatus,

	// Vacancy Page
	getVacancyDetails,
	getAvailableSlots,
	bookInterviewSlot,

	// Active Conversations
	checkActiveConversations,

	// Account Template
	getAccountTemplate,
	saveAccountTemplate,

	// Position Template
	getPositionTemplate,
	savePositionTemplate
};

export default aiRecruiterService;
